import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import api from '../../lib/api';
import { PostCard } from './PostCardKit';

const STARTERS = [
    {
        code: 'free', label: 'Publication libre', category: null,
        emoji: '✍️', desc: 'Vous écrivez librement.',
        title: '', body: '',
    },
    {
        code: 'seller_advice', label: 'Conseil vendeur', category: 'seller_advice',
        emoji: '🧭', desc: 'Une recommandation concrète à un propriétaire.',
        title: 'Le conseil que j\'aurais aimé donner plus tôt',
        body: "Constat observé sur le terrain :\n\nPourquoi cela arrive :\n\nCe que je recommande à un propriétaire :\n\nUne erreur à éviter :",
    },
    {
        code: 'local_analysis', label: 'Analyse locale', category: 'local_market_analysis',
        emoji: '📍', desc: 'Un constat sur votre secteur, avec une conséquence claire.',
        title: 'Ce que les chiffres disent vraiment sur [quartier]',
        body: "Ce que j'observe ce mois-ci sur [quartier] :\n\nPourquoi :\n\nConséquences pour un vendeur :\n\nMa lecture en deux phrases :",
    },
    {
        code: 'experience', label: 'Retour d\'expérience', category: 'professional_experience',
        emoji: '💬', desc: 'Une situation que vous avez gérée et qui éclaire un vendeur.',
        title: '',
        body: "Le contexte :\n\nLa difficulté rencontrée :\n\nLa manière dont on l'a abordée :\n\nLe résultat :\n\nCe que ça apprend à un futur vendeur :",
    },
    {
        code: 'agency_news', label: 'Actualité d\'agence', category: 'agency_news',
        emoji: '🏛️', desc: 'Annonce simple, sans promesse commerciale.',
        title: 'Une nouvelle étape pour [agence]',
        body: "Ce qui se passe :\n\nPourquoi maintenant :\n\nCe que ça change pour les vendeurs et acquéreurs :",
    },
    {
        code: 'neighborhood', label: 'Vie du quartier', category: 'neighborhood_insight',
        emoji: '🌿', desc: 'Évolution, projet, ambiance — utile aux acheteurs locaux.',
        title: 'Ce qui change à [quartier]',
        body: "Ce qui change :\n\nDepuis quand :\n\nCe que ça apporte aux habitants :\n\nL'impact possible sur la valeur immobilière :",
    },
];

const CATEGORIES = [
    { code: "seller_advice", label: "Conseil vendeur",
      help: "Partagez une recommandation concrète permettant à un propriétaire de mieux préparer sa vente." },
    { code: "local_market_analysis", label: "Analyse du marché local",
      help: "Constat, explication, conséquence pour les vendeurs, conseil concret." },
    { code: "buyer_advice", label: "Conseil acquéreur", help: "Un conseil pratique pour quelqu'un qui cherche à acheter." },
    { code: "professional_experience", label: "Retour d'expérience", help: "Une situation pro qui éclaire un propriétaire." },
    { code: "neighborhood_insight", label: "Vie du quartier", help: "Évolution, projets, ambiance d'un quartier." },
    { code: "agency_news", label: "Actualité de l'agence", help: "Nouvelle ouverture, recrutement, événement." },
    { code: "career_milestone", label: "Étape professionnelle", help: "Anniversaire, formation, distinction." },
    { code: "property_highlight", label: "Mise en avant d'un bien",
      help: "Présentez ce qui rend le bien intéressant. Homly n'est pas un portail d'annonces." },
    { code: "buyer_search", label: "Recherche acquéreur", help: "Sans exposer de données personnelles." },
    { code: "local_event", label: "Événement local", help: "Salon, conférence, journée découverte." },
    { code: "professional_recommendation", label: "Recommandation professionnelle", help: "Un confrère, un notaire estimé." },
    { code: "general_real_estate", label: "Autre sujet immobilier", help: "" },
];

export function PostComposer({ open, onClose, onPublished, defaultTerritoryId }) {
    const [step, setStep] = useState("starter");
    const [starter, setStarter] = useState(null);
    const [category, setCategory] = useState("seller_advice");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [territoryId, setTerritoryId] = useState(defaultTerritoryId || "");
    const [advanced, setAdvanced] = useState(false);
    const [audience, setAudience] = useState("public");
    const [sellerRelevance, setSellerRelevance] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [territories, setTerritories] = useState([]);

    useEffect(() => {
        if (!open) return;
        // Reset to starter selection on each open
        setStep("starter");
        setStarter(null);
        api.get("/territories?limit=200")
           .then(r => setTerritories(r.data.items || []))
           .catch(() => setTerritories([]));
    }, [open]);

    const pickStarter = (s) => {
        setStarter(s);
        if (s.category) setCategory(s.category);
        if (s.title) setTitle(s.title);
        if (s.body) setBody(s.body);
        setStep("compose");
    };

    const cat = CATEGORIES.find(c => c.code === category);
    const canPublish = body.trim().length >= 50 && category && territoryId;

    const submit = async (asDraft) => {
        setBusy(true); setError("");
        try {
            const payload = {
                category, body: body.trim(), title: title.trim() || undefined,
                territory_ids: [territoryId], primary_territory_id: territoryId,
                audience: asDraft ? "draft" : audience,
                status: asDraft ? "draft" : "published",
                ...(sellerRelevance && { seller_relevance: sellerRelevance }),
            };
            const r = await api.post("/posts", payload);
            if (!asDraft && r.data.status === "draft") {
                await api.post(`/posts/${r.data.id}/publish`);
            }
            onPublished && onPublished(r.data);
            setStep("compose"); setBody(""); setTitle("");
            setCategory("seller_advice"); setAdvanced(false);
            setAudience("public"); setSellerRelevance("");
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || "Erreur lors de la publication.");
        } finally { setBusy(false); }
    };

    if (!open) return null;

    return (
        <div data-testid="post-composer"
             className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
             onClick={onClose}>
            <motion.div initial={{ y: 30 }} animate={{ y: 0 }}
                 className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                <header className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10">
                    <h3 className="font-semibold text-lg text-slate-900">Publier sur Homly</h3>
                    <button onClick={onClose} data-testid="composer-close-btn"
                            aria-label="Fermer" className="p-2 rounded hover:bg-slate-100">
                        <X className="w-5 h-5" />
                    </button>
                </header>

                {step === "starter" && (
                    <div className="p-5 space-y-3" data-testid="composer-starter-step">
                        <p className="text-sm text-slate-600">
                            Choisissez un point de départ. Vous restez libre de tout réécrire ensuite.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-2">
                            {STARTERS.map(s => (
                                <button key={s.code}
                                        type="button"
                                        onClick={() => pickStarter(s)}
                                        data-testid={`starter-${s.code}`}
                                        className="text-left p-3 rounded-xl border border-slate-200 hover:border-[#681793]/40 hover:bg-slate-50 transition">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg">{s.emoji}</span>
                                        <span className="font-semibold text-sm text-slate-800">{s.label}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-slate-400 italic">
                            Aucun gabarit ne force un format. Vous pouvez tout effacer après avoir choisi.
                        </p>
                    </div>
                )}

                {step === "compose" && (
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                Que souhaitez-vous partager ?
                            </label>
                            <select data-testid="composer-category-select"
                                    value={category} onChange={e => setCategory(e.target.value)}
                                    className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-700">
                                {CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                            </select>
                            {cat?.help && (
                                <p className="mt-1.5 text-xs text-slate-500 italic">{cat.help}</p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                Titre <span className="text-slate-400 font-normal">(facultatif)</span>
                            </label>
                            <Input data-testid="composer-title" value={title}
                                   onChange={e => setTitle(e.target.value)}
                                   placeholder="Ex. : Pourquoi les acquéreurs hésitent à Lyon ce trimestre" />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                Votre message
                            </label>
                            <Textarea data-testid="composer-body" rows={7} value={body}
                                      onChange={e => setBody(e.target.value)}
                                      placeholder="Écrivez quelque chose d'utile pour les vendeurs ou acquéreurs de votre secteur…"
                                      className="text-sm" />
                            <div className="text-xs text-slate-400 mt-1">
                                {body.length} caractères (50 minimum)
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">
                                Territoire concerné
                            </label>
                            <select data-testid="composer-territory-select"
                                    value={territoryId} onChange={e => setTerritoryId(e.target.value)}
                                    className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-700">
                                <option value="">Sélectionnez une commune…</option>
                                {territories.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>

                        <button onClick={() => setAdvanced(!advanced)}
                                data-testid="composer-advanced-toggle"
                                className="text-sm text-[#681793] hover:underline">
                            {advanced ? "Masquer" : "Afficher"} les options avancées
                        </button>
                        {advanced && (
                            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                                <div>
                                    <label className="text-xs font-medium text-slate-600 mb-1 block">Audience</label>
                                    <select value={audience} onChange={e => setAudience(e.target.value)}
                                            data-testid="composer-audience"
                                            className="w-full p-2 border border-slate-300 rounded text-sm bg-white text-slate-700">
                                        <option value="public">Publique</option>
                                        <option value="subscribers">Mes abonnés</option>
                                        <option value="agency">Mon agence</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                                        Pertinence vendeur <span className="text-slate-400 font-normal">(automatique sinon)</span>
                                    </label>
                                    <select value={sellerRelevance}
                                            onChange={e => setSellerRelevance(e.target.value)}
                                            className="w-full p-2 border border-slate-300 rounded text-sm bg-white text-slate-700">
                                        <option value="">Automatique</option>
                                        <option value="low">Faible</option>
                                        <option value="medium">Moyenne</option>
                                        <option value="high">Élevée</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>
                        )}

                        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
                            <Button variant="ghost" disabled={busy || !body.trim()}
                                    data-testid="composer-save-draft-btn"
                                    onClick={() => submit(true)}>
                                Enregistrer en brouillon
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" disabled={!canPublish}
                                        data-testid="composer-preview-btn"
                                        onClick={() => setStep("preview")}>
                                    Aperçu
                                </Button>
                                <Button disabled={!canPublish || busy}
                                        data-testid="composer-publish-btn"
                                        onClick={() => submit(false)}
                                        className="bg-[#681793] hover:bg-[#5a1380]">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === "preview" && (
                    <div className="p-5 space-y-4">
                        <div className="text-sm text-slate-500">Aperçu avant publication</div>
                        <PostCard post={{
                            id: "preview", content_id: "preview",
                            created_at: new Date().toISOString(),
                            territory_id: territoryId,
                            metadata: { title, body_preview: body,
                                category_label: cat?.label,
                                reactions_count: 0, comments_count: 0 },
                        }} />
                        <div className="flex justify-between">
                            <Button variant="outline" onClick={() => setStep("compose")}>
                                <ChevronLeft className="w-4 h-4 mr-1" /> Modifier
                            </Button>
                            <Button onClick={() => submit(false)} disabled={busy}
                                    data-testid="composer-confirm-publish-btn"
                                    className="bg-[#681793] hover:bg-[#5a1380]">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer et publier"}
                            </Button>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
